/**
 * The Watchlist's workflow, in plain words (roadmap D163): where an entry
 * stands on its lifecycle, what moves it on, the one thing to do next, how
 * it can be settled and what settling writes.
 *
 *   A risk:   Identified → Assessed → Treating → Monitoring → Settled
 *   An issue: Open → Working → Resolved
 *   A bug:    Open → Fixing → Fixed (or Won't fix, Duplicate)
 *
 * The steps lie over the contract's statuses (shared/schemas/projectRisks.ts),
 * which do not change: a risk `open` is Identified (Assessed once it is
 * rated), `analysed` and `escalated` are Assessed, `mitigating` is Treating,
 * `monitoring` Monitoring, and `accepted`, `occurred` and `closed` are
 * Settled; an issue's and a bug's `in-progress` is Working or Fixing.
 *
 * Settling writes only fields the entry already has: the status (the home
 * stamps `closedAt` and who changed it), `disposition: tolerate` when a risk
 * is accepted, the pull request when a bug is fixed, the review date, and one
 * line on the response — "Settled 2026-10-04 — Accepted: <the reason>" —
 * which is how the sheet tells "resolved" from "no longer relevant" (both
 * `closed`) and shows the reason later ({@link settlement}).
 *
 * The owner is a person who answers for the entry; the treatment is the work
 * given out for it — tasks on a board, to a team, an agent or whoever takes
 * them ({@link responsibility}).
 *
 * DOM-free and tested (test/riskWorkflowModel.test.ts).
 */
// Functions only: a tsx test cannot value-import a shared constant (it comes through as CommonJS).
import { activeTreatment, closureOffer } from '@aioe/shared/risks/assign';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import type { RiskEntry, RiskKind, RiskPatch, RiskStatus, RiskTreatment } from '@aioe/shared';

// ── The lifecycle ────────────────────────────────────────────────────────

export type StageId = 'identified' | 'assessed' | 'treating' | 'monitoring' | 'settled' | 'open' | 'working' | 'resolved' | 'fixing' | 'fixed';

export interface LifecycleStep {
  id: StageId;
  label: string;
  /** What moves it on from this step, in a sentence. */
  movesOn: string;
  state: 'done' | 'current' | 'ahead';
}

export interface Lifecycle {
  steps: LifecycleStep[];
  /** The index of the current step. */
  current: number;
  /** A word beside the strip when the status says more than the step: "Escalated". */
  flag?: string;
}

type EntryLike = Pick<RiskEntry, 'kind' | 'status'> & Partial<Pick<RiskEntry, 'inherent' | 'disposition' | 'treatments' | 'reviewDue' | 'reviewedAt' | 'response' | 'closedAt' | 'updatedAt' | 'updatedBy' | 'pullRequest' | 'id' | 'uid' | 'title'>>;

const RISK_STEPS: ReadonlyArray<Omit<LifecycleStep, 'state'>> = [
  { id: 'identified', label: 'Identified', movesOn: 'Moves on when it is rated: how likely it is and what it would cost.' },
  { id: 'assessed', label: 'Assessed', movesOn: 'Moves on when the work to deal with it is assigned, or it is accepted.' },
  { id: 'treating', label: 'Treating', movesOn: 'Moves on when that work is done and the risk is rated again.' },
  { id: 'monitoring', label: 'Monitoring', movesOn: 'Looked at again on each review date. Moves on when it is settled.' },
  { id: 'settled', label: 'Settled', movesOn: 'Resolved, accepted, happened or no longer relevant. Reopen it if it comes back.' },
];
const ISSUE_STEPS: ReadonlyArray<Omit<LifecycleStep, 'state'>> = [
  { id: 'open', label: 'Open', movesOn: 'Moves on when someone is given the work.' },
  { id: 'working', label: 'Working', movesOn: 'Moves on when it is resolved, or it is decided not to fix it.' },
  { id: 'resolved', label: 'Resolved', movesOn: 'Reopen it if it comes back.' },
];
const BUG_STEPS: ReadonlyArray<Omit<LifecycleStep, 'state'>> = [
  { id: 'open', label: 'Open', movesOn: 'Moves on when someone is given the fix.' },
  { id: 'fixing', label: 'Fixing', movesOn: 'Moves on when it is fixed, will not be fixed, or turns out to be a duplicate.' },
  { id: 'fixed', label: 'Fixed', movesOn: 'Reopen it if it comes back.' },
];

/** The step an entry is at, from its status (and, for a risk still `open`, whether it has been rated). */
export function stageOf(e: Pick<EntryLike, 'kind' | 'status' | 'inherent'>): StageId {
  if (e.kind === 'risk') {
    switch (e.status) {
      case 'open':
        return e.inherent ? 'assessed' : 'identified';
      case 'analysed':
      case 'escalated':
        return 'assessed';
      case 'mitigating':
        return 'treating';
      case 'monitoring':
        return 'monitoring';
      default:
        return 'settled';
    }
  }
  if (e.status === 'open') return 'open';
  if (e.status === 'in-progress') return e.kind === 'bug' ? 'fixing' : 'working';
  return e.kind === 'bug' ? 'fixed' : 'resolved';
}

/** Whether the entry is settled: off the open list, kept as the record. */
export function isSettled(e: Pick<EntryLike, 'kind' | 'status' | 'inherent'>): boolean {
  const s = stageOf(e);
  return s === 'settled' || s === 'resolved' || s === 'fixed';
}

const LAST_LABEL: Partial<Record<RiskStatus, string>> = { 'wont-fix': 'Won’t fix', duplicate: 'Duplicate', accepted: 'Accepted', occurred: 'Happened' };

/** The strip at the top of an entry: every step, the current one marked, each saying what moves it on. */
export function lifecycle(e: Pick<EntryLike, 'kind' | 'status' | 'inherent'>): Lifecycle {
  const base = e.kind === 'risk' ? RISK_STEPS : e.kind === 'bug' ? BUG_STEPS : ISSUE_STEPS;
  const stage = stageOf(e);
  const current = Math.max(0, base.findIndex((s) => s.id === stage));
  const steps = base.map((s, i): LifecycleStep => ({ ...s, state: i < current ? 'done' : i === current ? 'current' : 'ahead' }));
  // The last step names how it ended when that is not the step's own word.
  const last = steps[steps.length - 1];
  if (current === steps.length - 1 && LAST_LABEL[e.status] && e.kind !== 'risk') steps[steps.length - 1] = { ...last, label: LAST_LABEL[e.status]! };
  return { steps, current, ...(e.kind === 'risk' && e.status === 'escalated' ? { flag: 'Escalated' } : {}) };
}

// ── Settling ─────────────────────────────────────────────────────────────

export type SettleOutcome = 'resolved' | 'accepted' | 'occurred' | 'not-relevant' | 'wont-fix' | 'fixed' | 'duplicate';

export interface SettleOption {
  id: SettleOutcome;
  label: string;
  /** What it means, in a sentence. */
  hint: string;
  status: RiskStatus;
  /** Whether a note is needed ("required": a reason), wanted ("optional") or not asked. */
  note: 'required' | 'optional';
  /** What else it asks for. */
  asks?: 'pull-request' | 'duplicate-of' | 'raise-issue';
}

const OPTIONS: Record<RiskKind, readonly SettleOption[]> = {
  risk: [
    { id: 'resolved', label: 'Resolved', hint: 'It was treated and what is left is within what the project accepts.', status: 'closed', note: 'optional' },
    { id: 'accepted', label: 'Accepted', hint: 'Live with it as it is. Say why: it stays on the record.', status: 'accepted', note: 'required' },
    { id: 'occurred', label: 'It happened', hint: 'It is no longer a risk: it is an issue now. Raise the issue so the work goes on there.', status: 'occurred', note: 'optional', asks: 'raise-issue' },
    { id: 'not-relevant', label: 'No longer relevant', hint: 'The plan changed, or what it threatened is gone.', status: 'closed', note: 'optional' },
  ],
  issue: [
    { id: 'resolved', label: 'Resolved', hint: 'It is dealt with: fixed, or worked around for good.', status: 'resolved', note: 'optional' },
    { id: 'wont-fix', label: 'Won’t fix', hint: 'It will be left as it is. Say why.', status: 'wont-fix', note: 'required' },
  ],
  bug: [
    { id: 'fixed', label: 'Fixed', hint: 'The fix is in. Link the pull request that has it.', status: 'fixed', note: 'optional', asks: 'pull-request' },
    { id: 'wont-fix', label: 'Won’t fix', hint: 'It will be left as it is. Say why.', status: 'wont-fix', note: 'required' },
    { id: 'duplicate', label: 'Duplicate', hint: 'Another bug already covers it: pick that one.', status: 'duplicate', note: 'optional', asks: 'duplicate-of' },
  ],
};

/** How an entry of this kind can be settled. */
export function settleOptions(kind: RiskKind): readonly SettleOption[] {
  return OPTIONS[kind];
}

export interface SettleInput {
  outcome: SettleOutcome;
  note: string;
  /** A bug fixed: the pull request (a link or `#n`). */
  pullRequest?: string;
  /** A duplicate: the other bug's number (`BG-3`). */
  duplicateOf?: string;
}

/** Why it cannot be settled like this yet, in words; undefined when it can. */
export function settleProblem(kind: RiskKind, input: SettleInput): string | undefined {
  const option = OPTIONS[kind].find((o) => o.id === input.outcome);
  if (!option) return 'Choose how it was settled.';
  if (option.note === 'required' && !input.note.trim()) return option.id === 'accepted' ? 'Say why it is accepted: the reason stays on the record.' : 'Say why it will not be fixed.';
  if (option.asks === 'duplicate-of' && !input.duplicateOf) return 'Pick the bug it duplicates.';
  return undefined;
}

const SETTLED_LINE = /^Settled (\d{4}-\d{2}-\d{2}) — ([^:\n]+?)(?:: (.*))?$/gm;
const REOPENED_LINE = /^Reopened (\d{4}-\d{2}-\d{2})/gm;

const withLine = (response: string | undefined, line: string) => (response?.trim() ? `${response.trim()}\n\n${line}` : line).slice(-4000);

/**
 * What settling writes: the status, the line on the response, the review
 * date, and — by outcome — the disposition (accepted), the pull request
 * (fixed). Settling as "It happened" here only marks the risk; raising the
 * issue (which the home marks it happened for) is the dialog's primary way.
 */
export function settlePatch(e: Pick<EntryLike, 'kind' | 'response' | 'pullRequest'>, input: SettleInput, today: string): RiskPatch {
  const option = OPTIONS[e.kind].find((o) => o.id === input.outcome);
  if (!option) return {};
  const note = input.note.replace(/\s+/g, ' ').trim();
  const detail = input.outcome === 'duplicate' && input.duplicateOf ? `duplicate of ${input.duplicateOf}${note ? `. ${note}` : ''}` : note;
  const line = `Settled ${today} — ${option.label}${detail ? `: ${detail}` : ''}`;
  const pr = input.pullRequest?.trim();
  return {
    status: option.status,
    response: withLine(e.response, line),
    reviewedAt: today,
    ...(e.kind === 'risk' && input.outcome === 'accepted' ? { disposition: 'tolerate' as const } : {}),
    ...(e.kind === 'bug' && input.outcome === 'fixed' && pr && pr !== e.pullRequest ? { pullRequest: pr.slice(0, 500) } : {}),
  };
}

export interface Settlement {
  outcome: SettleOutcome;
  label: string;
  /** The note or reason given, when there was one. */
  note?: string;
  /** The date the line was written (ISO date), else when the home says it closed. */
  on?: string;
  /** Who settled it, when the home's "last changed by" is that change. */
  by?: RiskEntry['updatedBy'];
}

const BY_STATUS: Partial<Record<RiskKind, Partial<Record<RiskStatus, SettleOutcome>>>> = {
  risk: { accepted: 'accepted', occurred: 'occurred', closed: 'resolved' },
  issue: { resolved: 'resolved', 'wont-fix': 'wont-fix' },
  bug: { fixed: 'fixed', 'wont-fix': 'wont-fix', duplicate: 'duplicate' },
};

/** How a settled entry was settled: from its status and the last "Settled …" line, who by and when. Undefined while it is open. */
export function settlement(e: EntryLike): Settlement | undefined {
  if (!isSettled(e)) return undefined;
  const lines = [...(e.response ?? '').matchAll(SETTLED_LINE)];
  const reopened = [...(e.response ?? '').matchAll(REOPENED_LINE)].map((m) => m.index ?? 0);
  const last = lines[lines.length - 1];
  const fresh = last && !reopened.some((i) => i > (last.index ?? 0)) ? last : undefined;
  const byStatus = BY_STATUS[e.kind]?.[e.status];
  const named = fresh ? OPTIONS[e.kind].find((o) => o.label === fresh[2].trim()) : undefined;
  // The line counts only when it agrees with the status (someone may have moved the status by hand since).
  const outcome = named && named.status === e.status ? named.id : (byStatus ?? OPTIONS[e.kind][0].id);
  const option = OPTIONS[e.kind].find((o) => o.id === outcome)!;
  const on = (named && named.status === e.status ? fresh?.[1] : undefined) ?? e.closedAt?.slice(0, 10);
  const note = named && named.status === e.status ? fresh?.[3]?.trim() : undefined;
  const by = settledBy(e);
  return { outcome, label: option.label, ...(note ? { note } : {}), ...(on ? { on } : {}), ...(by ? { by } : {}) };
}

/** Who settled it: the home's "last changed by", when that change is the one that settled it (within two minutes of `closedAt`). */
export function settledBy(e: Pick<EntryLike, 'closedAt' | 'updatedAt' | 'updatedBy'>): RiskEntry['updatedBy'] | undefined {
  if (!e.closedAt || !e.updatedAt || !e.updatedBy) return undefined;
  const gap = Math.abs(Date.parse(e.updatedAt) - Date.parse(e.closedAt));
  return Number.isFinite(gap) && gap <= 120_000 ? e.updatedBy : undefined;
}

/** "Reopen": back to the step before it was settled — a rated risk to Assessed, else the first — with a line on the record. */
export function reopenPatch(e: Pick<EntryLike, 'kind' | 'inherent' | 'response'>, today: string): RiskPatch {
  const status: RiskStatus = e.kind === 'risk' && e.inherent ? 'analysed' : 'open';
  return { status, response: withLine(e.response, `Reopened ${today}`) };
}

// ── The small moves ──────────────────────────────────────────────────────

/** "Mark reviewed": looked at today, next look on the cadence. */
export function reviewedPatch(today: string, nextReview: string): RiskPatch {
  return { reviewedAt: today, reviewDue: nextReview };
}

/** "Start monitoring": the treatment is done and the rating stands. */
export function monitorPatch(e: Pick<EntryLike, 'reviewDue'>, today: string, nextReview: string): RiskPatch {
  return { status: 'monitoring', reviewedAt: today, ...(e.reviewDue && e.reviewDue >= today ? {} : { reviewDue: nextReview }) };
}

/**
 * What saving the form moves along by itself: a risk rated for the first
 * time while Identified becomes Assessed; a risk whose treatment is done,
 * rated again, goes to Monitoring. Returns the patch with the status added
 * and the words for it; the patch as it was when nothing moves.
 */
export function withWorkflowMoves(e: EntryLike, patch: RiskPatch, today: string, nextReview: string): { patch: RiskPatch; moved?: string } {
  if (e.kind !== 'risk' || patch.status !== undefined) return { patch };
  if (e.status === 'open' && !e.inherent && patch.inherent) return { patch: { ...patch, status: 'analysed' }, moved: 'It is rated: now decide what to do about it.' };
  if (e.status === 'mitigating' && patch.residual && closureOffer(e)) return { patch: { ...patch, ...monitorPatch(e, today, nextReview) }, moved: 'Rated again with the work in place: it is now being monitored.' };
  return { patch };
}

/** The four ways to deal with a risk, in plain words, for the "Decide what to do" step. */
export const DECISIONS: ReadonlyArray<{ id: NonNullable<RiskEntry['disposition']>; label: string; hint: string }> = [
  { id: 'treat', label: 'Reduce it', hint: 'Put work in place that makes it less likely or less costly.' },
  { id: 'tolerate', label: 'Live with it', hint: 'Accept it as it is, with a reason, and keep it on the record.' },
  { id: 'transfer', label: 'Hand it on', hint: 'Give it to someone better placed: a supplier, insurance, another team.' },
  { id: 'terminate', label: 'Stop the cause', hint: 'Stop doing the thing that causes it.' },
];

/** A disposition in plain words ("Reduce it"). */
export function decisionWords(d: RiskEntry['disposition'] | undefined): string | undefined {
  return d ? DECISIONS.find((x) => x.id === d)?.label : undefined;
}

// ── Who: the owner and the work ──────────────────────────────────────────

/** A treatment as the sheet lists it: the task, who has it and how it stands. */
export interface TreatmentLine {
  uid: string;
  rootId: string;
  board: string;
  taskId?: string;
  title?: string;
  /** Who has it: the agent or team, or "whoever takes it from the board". */
  who: string;
  /** How it stands, in words: the board's column when known, else how the treatment ended. */
  state: string;
  /** Still being worked on. */
  live: boolean;
  summary?: string;
}

/** What the board says about a task, when this view can see it. */
export type BoardLookup = (uid: string) => { id?: string; status?: string; assignee?: string } | undefined;

const COLUMN_WORDS: Record<string, string> = { todo: 'to do', 'in-progress': 'in progress', review: 'in review', blocked: 'blocked', done: 'done', backlog: 'in the backlog' };

export function treatmentLines(list: readonly RiskTreatment[] | undefined, board?: BoardLookup): TreatmentLine[] {
  return [...(list ?? [])].reverse().map((t) => {
    const live = t.state === 'assigned';
    const seen = board?.(t.task.uid);
    const who = seen?.assignee ?? t.assignee ?? 'whoever takes it from the board';
    const state = live ? (seen?.status ? (COLUMN_WORDS[seen.status] ?? seen.status) : 'assigned') : t.state === 'done' ? 'done' : 'stopped';
    return { uid: t.task.uid, rootId: t.task.rootId, board: t.task.board, ...(t.taskId ? { taskId: t.taskId } : {}), ...(t.title ? { title: t.title } : {}), who, state, live, ...(t.summary ? { summary: t.summary } : {}) };
  });
}

/** Who the work is with now — "Security reviewer", "the board" — or undefined when nothing is being worked on. */
export function treatedBy(e: Partial<Pick<RiskEntry, 'treatments'>>): string | undefined {
  const active = activeTreatment(e);
  if (!active) return undefined;
  return active.assignee ?? 'the board';
}

/** One line on who does what: the owner answers for it, the work is with whoever has the task. */
export function responsibility(e: EntryLike, owner: string | undefined): string {
  const active = activeTreatment(e);
  const done = [...(e.treatments ?? [])].reverse().find((t) => t.state === 'done');
  const work = active
    ? `The work is with ${active.assignee ?? 'whoever takes it from the board'}${active.taskId ? ` (${active.taskId})` : ''}.`
    : isSettled(e)
      ? ''
      : done
        ? `The work is done${done.assignee ? ` (${done.assignee}${done.taskId ? `, ${done.taskId}` : ''})` : done.taskId ? ` (${done.taskId})` : ''}.`
        : 'Nobody has been given the work yet.';
  const who = owner ? `${owner} answers for it.` : isSettled(e) ? '' : 'Nobody answers for it yet: name an owner in Details.';
  return [who, work].filter(Boolean).join(' ');
}

// ── What to do next ──────────────────────────────────────────────────────

export type WorkflowAction = 'assess' | 'assign' | 'reassess' | 'monitor' | 'reviewed' | 'settle' | 'reopen' | 'raise-issue' | 'undecide';

export interface WorkflowButton {
  action: WorkflowAction;
  label: string;
  /** For `settle`: the outcome the dialog opens on. */
  outcome?: SettleOutcome;
}

export interface NextStep {
  stage: StageId;
  title: string;
  text: string;
  /** The one primary action. */
  primary?: WorkflowButton;
  /** At most one quieter one beside it. */
  secondary?: WorkflowButton;
  /** Show the four decisions to choose from (a risk at Assessed with none made). */
  decide?: boolean;
  /** The work given out, for Treating / Working / Fixing. */
  showTreatments?: boolean;
  /** How it was settled, once it is. */
  settled?: Settlement;
}

export interface NextStepContext {
  /** The next review in words ("12 Oct"), and whether it has passed. */
  review?: { text: string; overdue: boolean };
  /** A risk above what its level accepts. */
  aboveAppetite?: boolean;
  /** A risk that happened: an issue already names it. */
  hasIssue?: boolean;
  /** The current rating in words ("High 16"), for a sentence. */
  rating?: string;
}

const short = (iso: string | undefined) => {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : dateMedium(d, { timeZone: 'UTC' });
};

/** The card under the strip: for where the entry is, what to do now and the one action that does it. */
export function nextStep(e: EntryLike, ctx: NextStepContext = {}): NextStep {
  const stage = stageOf(e);
  const active = activeTreatment(e);
  const offer = closureOffer(e);
  const stopped = !active && !offer && (e.treatments ?? []).some((t) => t.state === 'stopped');

  if (isSettled(e)) {
    const s = settlement(e);
    const how = s ? `${s.label}${s.on ? ` on ${short(s.on)}` : ''}.` : 'Settled.';
    const text = [how, s?.note ? `“${s.note}”` : '', 'It stays on the Watchlist as the record. Reopen it if it comes back.'].filter(Boolean).join(' ');
    if (e.kind === 'risk' && e.status === 'occurred' && !ctx.hasIssue)
      return { stage, title: 'It happened', text: `${text} No issue names it yet: raise one so the work goes on there.`, primary: { action: 'raise-issue', label: 'Raise an issue from it' }, secondary: { action: 'reopen', label: 'Reopen' }, ...(s ? { settled: s } : {}) };
    return { stage, title: s ? `Settled: ${s.label}` : 'Settled', text, primary: { action: 'reopen', label: 'Reopen' }, ...(s ? { settled: s } : {}) };
  }

  if (e.kind === 'risk') {
    if (stage === 'identified')
      return { stage, title: 'Assess it', text: 'Rate how likely it is and what it would cost if it happened, before anything is done about it. Saving the rating moves it to Assessed.', primary: { action: 'assess', label: 'Assess it' } };
    if (stage === 'assessed' && !active) {
      const rated = ctx.rating ? `It is rated ${ctx.rating}. ` : '';
      const appetite = ctx.aboveAppetite ? 'It is above what the project accepts, so living with it needs a good reason. ' : '';
      if (!e.disposition)
        return {
          stage,
          title: e.status === 'escalated' ? 'Waiting on a decision' : 'Decide what to do',
          text: `${e.status === 'escalated' ? 'It was escalated past the owner: whoever it went to decides. ' : ''}${rated}${appetite}Choose one: reduce it, live with it, hand it on or stop what causes it.`,
          decide: true,
        };
      if (e.disposition === 'tolerate')
        return { stage, title: 'Accept it', text: `You chose to live with it. ${appetite}Accepting it settles it, with your reason on the record; it can be reopened.`, primary: { action: 'settle', label: 'Accept it…', outcome: 'accepted' }, secondary: { action: 'undecide', label: 'Change the decision' } };
      const what = e.disposition === 'transfer' ? 'handing it on' : e.disposition === 'terminate' ? 'stopping what causes it' : 'reducing it';
      return { stage, title: 'Assign the treatment', text: `You chose ${what}. Give that work to a team or an agent as a task, or leave it on the board for whoever takes it. Assigning it moves the risk to Treating.`, primary: { action: 'assign', label: 'Assign…' }, secondary: { action: 'undecide', label: 'Change the decision' } };
    }
    if (stage === 'treating' || (stage === 'assessed' && active)) {
      if (active) return { stage, title: 'Being treated', text: 'The work is on the board. When it is done, rate the risk again with what was put in place.', showTreatments: true, secondary: { action: 'assign', label: 'Assign more work…' } };
      if (offer)
        return {
          stage,
          title: 'Rate it again',
          text: `${offer.treatment.taskId ?? 'The task'} is done${offer.treatment.assignee ? ` (${offer.treatment.assignee})` : ''}.${offer.treatment.summary ? ` ${offer.treatment.summary}` : ''} Rate what is left now the work is in place; saving the new rating moves it to Monitoring.`,
          primary: { action: 'reassess', label: 'Re-assess the residual' },
          secondary: { action: 'monitor', label: 'Keep the rating, start monitoring' },
          showTreatments: true,
        };
      return { stage, title: 'Assign the treatment', text: stopped ? 'The last task given out for it was stopped, so nothing is being done about it. Give the work out again.' : 'Nothing is being done about it yet. Give the work to a team or an agent as a task.', primary: { action: 'assign', label: 'Assign…' }, ...(stopped ? { showTreatments: true } : {}) };
    }
    // Monitoring.
    const review = ctx.review ? (ctx.review.overdue ? `The review is ${ctx.review.text.replace(/^review /, '')}.` : `Next review ${ctx.review.text.replace(/^review /, '')}.`) : 'No review date is set.';
    return { stage, title: 'Keep an eye on it', text: `${review} Look at it again: if nothing has changed, mark it reviewed. When it no longer needs watching, settle it.`, primary: { action: 'reviewed', label: 'Mark reviewed' } };
  }

  // An issue or a bug.
  const bug = e.kind === 'bug';
  if (active) return { stage, title: bug ? 'Being fixed' : 'Being worked on', text: 'The work is on the board. When it is done, settle it here.', showTreatments: true, secondary: { action: 'assign', label: 'Assign more work…' } };
  if (offer)
    return {
      stage,
      title: bug ? 'Is it fixed?' : 'Is it resolved?',
      text: `${offer.treatment.taskId ?? 'The task'} is done${offer.treatment.assignee ? ` (${offer.treatment.assignee})` : ''}.${offer.treatment.summary ? ` ${offer.treatment.summary}` : ''}`,
      primary: bug ? { action: 'settle', label: 'Mark it fixed…', outcome: 'fixed' } : { action: 'settle', label: 'Resolve it…', outcome: 'resolved' },
      secondary: { action: 'assign', label: 'Assign more work…' },
      showTreatments: true,
    };
  return {
    stage,
    title: bug ? 'Get it fixed' : 'Assign it',
    text: `${stopped ? 'The last task given out for it was stopped. ' : ''}Give ${bug ? 'the fix' : 'the work'} to a team or an agent as a task, or leave it on the board for whoever takes it. Assigning it moves it to ${bug ? 'Fixing' : 'Working'}.`,
    primary: { action: 'assign', label: 'Assign…' },
    ...(stopped ? { showTreatments: true } : {}),
  };
}

// ── The help ─────────────────────────────────────────────────────────────

/** "How the Watchlist works", in five lines. */
export const HOW_IT_WORKS: readonly string[] = [
  'Raise it. A risk may happen; an issue has happened; a bug is something built that is broken.',
  'Rate it. How likely and how costly: the rating says how much it matters.',
  'Decide and assign. Reduce it, live with it, hand it on or stop the cause — and give the work to a team or an agent as a task.',
  'Watch it. When the work is done, rate it again, then look at it on each review date.',
  'Settle it. Resolved, accepted with a reason, happened (raise an issue), or no longer relevant — recorded with who and when.',
];

/** The owner and the work, told apart in one sentence. */
export const OWNER_AND_WORK = 'The owner is the person who answers for it; the work is a task given to a team or an agent.';

// ── The picture's explainer ──────────────────────────────────────────────

/** A place on the Watchlist's picture: a step of a lane, a part of the treatment branch, or a way to settle. */
export type MapPoint = { kind: RiskKind; stage: StageId } | { kind: 'risk'; branch: 'assign' | 'task' | 'reassess' } | { kind: RiskKind; outcome: SettleOutcome };

export interface Explainer {
  /** "Risk · Treating". */
  title: string;
  means: string;
  /** Who does it: the owner (a person) or the team or agent with the task. */
  who: string;
  movesOn: string;
  /** "Show me" filters the list to entries at this step. */
  showMe?: { kind: RiskKind; stage: StageId; label: string };
}

const KIND_WORD: Record<RiskKind, string> = { risk: 'Risk', issue: 'Issue', bug: 'Bug' };
const KIND_PLURAL: Record<RiskKind, string> = { risk: 'risks', issue: 'issues', bug: 'bugs' };

const MEANS: Record<string, { means: string; who: string }> = {
  'risk:identified': { means: 'Something that may go wrong has been written down. Nothing is known yet about how likely it is or what it would cost.', who: 'Whoever spotted it raised it — a person, or an agent while it worked. A person is named as its owner: they answer for it from here on.' },
  'risk:assessed': { means: 'It is rated: how likely it is and how much it would hurt, before and after what is already in place. Now someone decides what to do about it — reduce it, live with it, hand it on, or stop the cause.', who: 'The owner decides. An agent can be asked to rate it, but the decision is a person’s.' },
  'risk:treating': { means: 'Work to make it less likely or less costly is under way, as tasks on a board.', who: 'The team or agent given the task does the work. The owner still answers for the risk and checks the result.' },
  'risk:monitoring': { means: 'The work is done and the risk is rated again. It stays open so someone looks at it on each review date, in case it grows.', who: 'The owner, on each review date. When one passes, the Inbox asks them.' },
  'risk:settled': { means: 'It no longer needs watching: it was resolved, accepted with a reason, it happened (and became an issue), or it stopped mattering. It stays on the Watchlist as the record.', who: 'The owner, or whoever settled it — recorded by name, with the date and the reason.' },
  'issue:open': { means: 'Something has gone wrong and is costing the project now.', who: 'Whoever found it recorded it. The owner, a person, answers for it.' },
  'issue:working': { means: 'Someone has the work of dealing with it: a fix, or a workaround meanwhile.', who: 'The team or agent with the task does the work; the owner answers for it.' },
  'issue:resolved': { means: 'It is dealt with, or it was decided to leave it as it is (with the reason why).', who: 'The owner, or whoever settled it — recorded with the date.' },
  'bug:open': { means: 'Something that was built is broken: how bad it is, how to make it happen, and what it affects.', who: 'Whoever found it recorded it — a person, a reviewer or a failing check. An owner answers for it.' },
  'bug:fixing': { means: 'Someone is fixing it: diagnosing, changing the code and checking the fix.', who: 'The team or agent with the task — a debugger suits most bugs; the owner answers for it.' },
  'bug:fixed': { means: 'The fix is in (with the pull request that has it), or it will not be fixed, or another bug already covers it.', who: 'The owner, or whoever settled it — recorded with the date.' },
};

const BRANCH: Record<'assign' | 'task' | 'reassess', { title: string; means: string; who: string; movesOn: string }> = {
  assign: { title: 'Assign the treatment', means: 'The work of dealing with the risk is given out as a task: to a team, one of its members, a specialist, or left on the board for whoever takes it.', who: 'The owner assigns it, from the risk’s next step.', movesOn: 'Assigning it moves the risk to Treating.' },
  task: { title: 'The task, with an agent or a team', means: 'The task sits on a board like any other and carries the risk with it. When it closes, what it says was done is written on the risk.', who: 'The agent or team with the task does the work. The owner still answers for the risk.', movesOn: 'When the task is done, the risk asks to be rated again.' },
  reassess: { title: 'Rate it again', means: 'With the work in place, how likely and how costly is it now? The new rating is what is left — the residual.', who: 'The owner, or an agent asked to.', movesOn: 'Saving the new rating moves the risk to Monitoring.' },
};

/** What a place on the picture means, who does it and what moves it on, with the "Show me" filter. */
export function explain(point: MapPoint): Explainer {
  if ('branch' in point) {
    const b = BRANCH[point.branch];
    return { title: `Risk · ${b.title}`, means: b.means, who: b.who, movesOn: b.movesOn, showMe: { kind: 'risk', stage: 'treating', label: 'risks being treated' } };
  }
  if ('outcome' in point) {
    const option = OPTIONS[point.kind].find((o) => o.id === point.outcome) ?? OPTIONS[point.kind][0];
    const settledStage: StageId = point.kind === 'risk' ? 'settled' : point.kind === 'bug' ? 'fixed' : 'resolved';
    const extra = point.kind === 'risk' && option.id === 'occurred' ? ' Raise an issue from it, and the work goes on there.' : option.note === 'required' ? ' A reason is needed: it stays on the record.' : '';
    return { title: `${KIND_WORD[point.kind]} · settled as ${option.label.toLowerCase()}`, means: `${option.hint}${extra}`, who: 'The owner, or whoever settles it — recorded by name, with the date.', movesOn: 'Settled is the end of the line. Reopen it if it comes back.', showMe: { kind: point.kind, stage: settledStage, label: `settled ${KIND_PLURAL[point.kind]}` } };
  }
  const steps = point.kind === 'risk' ? RISK_STEPS : point.kind === 'bug' ? BUG_STEPS : ISSUE_STEPS;
  const step = steps.find((s) => s.id === point.stage) ?? steps[0];
  const words = MEANS[`${point.kind}:${step.id}`] ?? { means: '', who: '' };
  return { title: `${KIND_WORD[point.kind]} · ${step.label}`, means: words.means, who: words.who, movesOn: step.movesOn, showMe: { kind: point.kind, stage: step.id, label: `${KIND_PLURAL[point.kind]} at ${step.label}` } };
}

/** The steps of a lane, for the picture: ids and labels in order. */
export function laneSteps(kind: RiskKind): Array<{ id: StageId; label: string }> {
  return (kind === 'risk' ? RISK_STEPS : kind === 'bug' ? BUG_STEPS : ISSUE_STEPS).map((s) => ({ id: s.id, label: s.label }));
}

/** Whether an entry is at a step of its lane (the "Show me" filter). */
export function atStage(e: Pick<EntryLike, 'kind' | 'status' | 'inherent'>, kind: RiskKind, stage: StageId): boolean {
  return e.kind === kind && stageOf(e) === stage;
}
